import dayjs from 'dayjs';
import startsWith from 'lodash/startsWith';
import React, { useState, useEffect, useMemo } from 'react';
import { Button, FormControl, OverlayTrigger, Popover } from 'react-bootstrap';
import { BsXLg, BsCheckLg } from 'react-icons/bs';
import InputMask from 'react-input-mask';
import PhoneInput from 'react-phone-input-2';

import './BayEditable.scss';

import BayDatepicker from '../BayDatepicker';
import { emailRegex } from '../utilities/regex';

//FIXME: https://github.com/bl00mber/react-phone-input-2/issues/609
const PhoneInputComponent = (PhoneInput as any).default
	? (PhoneInput as any).default
	: PhoneInput;

interface PhoneCountry {
	dialCode?: string;
	format?: string;
	name?: string;
	countryCode?: string;
}

interface BayEditableProps {
	className?: string;
	isDateOfBirth?: boolean;
	inputWidth?: string;
	onSubmitFunc?: (value: string) => void;
	onCloseFunc?: () => void;
	onChangeFunc?: (value: string) => void;
	useInputMask?: boolean;
	small?: boolean;
	mask?: string;
	value?: string;
	country?: PhoneCountry;
	onValidate?: (value: string) => boolean;
	errorText?: string;
	usePhoneInput?: boolean;
	isValidPattern?: boolean;
	validatePattern?: string | RegExp;
	maxLength?: number | null;
	isEnterKeySubmit?: boolean;
	isEmailPopover?: boolean;
	disabledInput?: boolean;
	isInvalidPattern?: boolean;
	mobileErrorText?: string;
	emailErrorText?: string;
	emailPattern?: RegExp;
	handleOnInputChange?: (value: string, country?: PhoneCountry) => void;
	isGroupNameExist?: boolean;
	countryCodeEditable?: boolean;
	defaultCountry?: string;
	showInitialErrorMsg?: boolean;
	textLimit?: boolean;
	textLimitLength?: number;
	showActionsBtn?: boolean;
	isPhoneRecipient?: boolean;
}

const BayEditable: React.FC<BayEditableProps> = ({
	className,
	isDateOfBirth,
	inputWidth = '18ch',
	onSubmitFunc = () => {},
	onCloseFunc = () => {},
	onChangeFunc,
	useInputMask = false,
	small = false,
	mask,
	value,
	country,
	onValidate,
	errorText,
	usePhoneInput = false,
	isValidPattern = false,
	validatePattern = '',
	maxLength = null,
	isEnterKeySubmit = false,
	isEmailPopover = false,
	disabledInput = false,
	isInvalidPattern = false,
	mobileErrorText = '',
	emailErrorText = '',
	// Defaults to the shared rule. This used to be /^$/, which matches only the
	// empty string, so email mode rejected every address a consumer did not pass
	// its own pattern for. A consumer-supplied pattern still wins
	// (monday 13197296743).
	emailPattern = emailRegex,
	handleOnInputChange = () => {},
	isGroupNameExist,
	countryCodeEditable = true,
	defaultCountry = 'us',
	showInitialErrorMsg = false,
	textLimit = false,
	textLimitLength = 20,
	showActionsBtn = true,
	isPhoneRecipient = false,
}) => {
	const [isValid, setIsValid] = useState(isInvalidPattern ?? true);
	const [isValidNumber, setIsValidNumber] = useState(true);
	const [isValidEmail, setIsValidEmail] = useState(true);
	const [editorValue, setEditorValue] = useState(value ? value : '');
	const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);
	const [textLimitError, setTextLimitError] = useState(false);

	// Memoize the pattern to avoid creating new RegExp on every keystroke
	const compiledPattern = useMemo(() => {
		if (!validatePattern) return null;
		return typeof validatePattern === 'string'
			? new RegExp(validatePattern)
			: validatePattern;
	}, [validatePattern]);

	useEffect(() => {
		let number = value;
		if (value?.length === 10 && usePhoneInput && !isPhoneRecipient) {
			number = '1' + value;
		}
		setEditorValue(number || '');
	}, [value, usePhoneInput, isPhoneRecipient]);

	useEffect(() => {
		setIsValid(isInvalidPattern);
	}, [isInvalidPattern]);

	const onClose = () => {
		onCloseFunc();
	};

	const onSubmit = () => {
		if (onValidate && !onValidate(editorValue)) {
			setIsValid(false);
		} else {
			// While click the Add section button on custom fields - isGroupNameExist - true
			// If custom field groupname already exist (Initially) that time we need show name already exist error so we need to submit.
			// For handling the above situation
			if (!isGroupNameExist && value === editorValue) {
				onClose();
				return;
			}
			onSubmitFunc(editorValue);
			setIsValid(true);
		}
	};

	const onSubmitDateOfBirth = () => {
		if (dateOfBirth) {
			onSubmitFunc(dateOfBirth);
		}
	};

	const onChange = (value: string) => {
		if (value && isValidPattern) {
			if (compiledPattern && !compiledPattern?.test(value)) {
				// Restrict the user to type unwanted type
				// Ex : We need only accept numbers or the alphabet - that time it will be helping
				return;
			}
		}
		if (isEmailPopover && value?.trim() && !emailPattern?.test(value)) {
			setIsValidEmail(false);
		} else {
			setIsValidEmail(true);
		}

		setIsValid(true);
		setIsValidNumber(true);

		if (onChangeFunc) {
			onChangeFunc(value);
		} else {
			if (handleOnInputChange) {
				handleOnInputChange(value, country);
			}
			setEditorValue(value);
		}
		if (textLimit) {
			value?.length > textLimitLength
				? setTextLimitError(true)
				: setTextLimitError(false);
		}
	};

	const popoverActivity = (
		<Popover
			className="container"
			id="popover-basic"
			style={{
				minWidth: 50,
				minHeight: 10,
				'--bs-popover-bg': '#2F323E',
				'--bs-popover-header-bg': '#2F323E',
				padding: 10,
				textAlign: 'center',
			} as React.CSSProperties}
		>
			<div
				className="d-flex justify-content-center"
				style={{ color: 'white', textAlign: 'center' }}
			>
				{isEmailPopover ? emailErrorText : mobileErrorText}
			</div>
		</Popover>
	);

	const isDisableBsCheckLgIcon =
		(showInitialErrorMsg && !editorValue?.trim()?.length) ||
		!isValid ||
		!isValidNumber ||
		!isValidEmail;

	const isInvalidPhoneNumber = !isPhoneRecipient && !isValidNumber;
	return (
		<>
			<div
				className={`bay-editable  ${className ?? ''} ${ showActionsBtn ? '' : 'bay-editable-without-actions-btn' } ${isPhoneRecipient ? 'bay-editable--recipient' : 'pe-2'}`}
				style={{ height: small ? 32 : 36 }}
			>
				{!isDateOfBirth ? (
					<>
						{usePhoneInput ? (
							<OverlayTrigger
								rootClose
								overlay={popoverActivity}
								placement="top"
								show={isInvalidPhoneNumber}
								trigger="click"
							>
								<PhoneInputComponent
									enableSearch
									countryCodeEditable={countryCodeEditable}
									inputClass={`${small ? 'form-control-sm ' : isPhoneRecipient ? 'form-control rounded-1' : ''} ${isInvalidPhoneNumber ? 'input bay-editable-input-error' : ''} `}
									inputProps={{
										name: 'PhoneNumber',
										id: 'PhoneNumber',
										required: true,
									}}
									inputStyle={{ width: inputWidth, height: small ? 32 : 36 }}
									value={editorValue}
									onChange={(value: string, phoneCountry: PhoneCountry, _event: React.ChangeEvent<HTMLInputElement>, formattedValue: string) => {
										if (!phoneCountry || !phoneCountry.format) {
											return; // guard against undefined country
										}

										const dialCode = phoneCountry.dialCode ?? '';
										const isEmpty = value === dialCode; // user cleared back to just dial code

										if (isPhoneRecipient) {
											if (isEmpty) {
												handleOnInputChange('', phoneCountry); // signal empty
												return;
											}

											// core validation: formattedValue must match country format length exactly
											const isComplete = phoneCountry.format.length === formattedValue?.length;
											if (!isComplete) {
												// partial input — update display but mark invalid
												setIsValidNumber(false);
												handleOnInputChange(value, phoneCountry); // still pass up so UI reflects typing
												return;
											}

											// valid complete number
											setIsValidNumber(true);
											handleOnInputChange(value, phoneCountry);
											return;
										}

										// existing non-recipient flow unchanged
										if (phoneCountry.format.length !== formattedValue?.length) {
											setIsValidNumber(false);
											return;
										}
										onChange(value);
									}}
									country={defaultCountry.toLowerCase()}
									//onlyCountries={['us', 'mx', 'ca']}
									isValid={(inputNumber: string, _country: unknown, countries: PhoneCountry[]) => {
										return countries.some((country) => {
											return (
												startsWith(inputNumber, country.dialCode) ||
												startsWith(country.dialCode, inputNumber)
											);
										});
									}}
								/>
							</OverlayTrigger>
						) : useInputMask ? (
							<InputMask
								className={`${small ? 'form-control form-control-sm ' : 'form-control rounded-1'} ${!isValid ? 'bay-editable-input-error' : ''}`}
								mask={mask ? mask : ''}
								style={{ width: inputWidth, height: small ? 32 : 36 }}
								value={editorValue}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value)}
							/>
						) : (
							<OverlayTrigger
								rootClose
								overlay={popoverActivity}
								placement="top"
								show={!isValidEmail}
								trigger="click"
							>
								<>
									<FormControl
										className={`${textLimit ? 'bay-form-control' : ''} ${small ? 'form-control form-control-sm ' : 'form-control rounded-1'} ${!isValid || textLimitError ? 'bay-editable-input-error' : ''}`}
										maxLength={maxLength || undefined}
										style={{ width: inputWidth }}
										value={editorValue}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.currentTarget.value)}
										onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
											if (event?.charCode === 13 && isEnterKeySubmit) {
												onSubmit();
											}
										}}
									/>
									{textLimit && (
										<div className="text-end mt-1 position-absolute text-limit-length">
											{editorValue?.length || 0}/{textLimitLength}
										</div>
									)}
								</>
							</OverlayTrigger>
						)}
					</>
				) : (
					<BayDatepicker
						yearAmount={100}
						onChangeFunc={(e: Date) => setDateOfBirth(dayjs(e).format('MM/DD/YYYY'))}
					/>
				)}

				{/* Hide the action buttons based on the `showActionsBtn` prop (default value is `true`) */}
				{(showActionsBtn && !isPhoneRecipient) && <>
					{!isDateOfBirth ? (
						<Button
							className={`${isDisableBsCheckLgIcon || textLimitError ? 'bay-editable-error' : ''} bay-editable-button bay-editable-button-save text-light ${small ? 'fs-5 p-1' : 'fs-4 px-2'}`}
							disabled={
								isDisableBsCheckLgIcon ||
								disabledInput ||
								!editorValue?.trim()?.length ||
								textLimitError
							}
							variant="success"
							onClick={() => onSubmit()}
						>
							<BsCheckLg />
						</Button>
					) : (
						<Button
							className={`${dateOfBirth === null ? 'bay-editable-error' : ''} bay-editable-button bay-editable-button-save text-light ${small ? 'fs-5 p-1' : 'fs-4 px-2'}`}
							disabled={dateOfBirth === null}
							variant="success"
							onClick={() => onSubmitDateOfBirth()}
						>
							<BsCheckLg />
						</Button>
					)}
					<Button
						className={`bay-editable-button bay-editable-button-cancel ${small ? 'fs-5 p-1' : 'fs-4 px-2'}`}
						variant="secondary"
						onClick={() => onClose()}
					>
						<BsXLg />
					</Button>
				</>}
			</div>
			{isDisableBsCheckLgIcon && !isPhoneRecipient && (
				<div className="fs-7 text-red mt-2">{errorText}</div>
			)}
		</>
	);
};

export default BayEditable;
